import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { Field, Input, Select, FormGrid, SectionLabel, SaveMessage, Card, CardHeader, SubmitBtn, SecondaryBtn, EmptyState, RecordMeta, Badge, Avatar, STATES, INDUSTRIES } from './Shared';

const CONTACT_TYPES = [{value:'client',label:'Client'},{value:'specialist',label:'Specialist / contractor'},{value:'broker',label:'Insurance broker'},{value:'referral',label:'Referral partner'},{value:'ahj',label:'AHJ contact'},{value:'corporate',label:'Corporate Academy'},{value:'other',label:'Other'}];
const TYPE_VARIANTS = { client:'blue', specialist:'green', broker:'amber', referral:'green', ahj:'red', corporate:'purple', other:'gray' };
const AVATAR_COLORS = { client:'avatar-blue', specialist:'avatar-green', broker:'avatar-amber', ahj:'avatar-red', corporate:'avatar-blue', other:'avatar-blue' };

export function ContactsPage({ profile }) {
  const [filter, setFilter] = useState('all');
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({ full_name:'', company:'', email:'', phone:'', state:'', contact_type:'client', notes:'' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ show:false, text:'', error:false });

  useEffect(() => { load(); }, [filter]);

  async function load() {
    let q = supabase.from('contacts').select('*').order('created_at', { ascending:false });
    if (filter !== 'all') q = q.eq('contact_type', filter);
    const { data } = await q;
    setRecords(data || []);
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function save() {
    if (!form.full_name) { setMsg({ show:true, text:'Name required.', error:true }); return; }
    setLoading(true);
    const { error } = await supabase.from('contacts').insert({
      ...form, submitted_by: profile.id, submitted_by_name: profile.full_name || profile.email
    });
    setLoading(false);
    if (error) { setMsg({ show:true, text:'Error: ' + error.message, error:true }); return; }
    setMsg({ show:true, text:'Contact saved.', error:false });
    setForm({ full_name:'', company:'', email:'', phone:'', state:'', contact_type:'client', notes:'' });
    load();
    setTimeout(() => setMsg({ show:false }), 3000);
  }

  return (
    <div>
      <Card>
        <SectionLabel>ADD CONTACT</SectionLabel>
        <FormGrid>
          <Field label="Full name"><Input value={form.full_name} onChange={set('full_name')} placeholder="Full name" /></Field>
          <Field label="Company"><Input value={form.company} onChange={set('company')} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={set('email')} /></Field>
          <Field label="Phone"><Input type="tel" value={form.phone} onChange={set('phone')} /></Field>
          <Field label="State"><Select value={form.state} onChange={set('state')} options={STATES} placeholder="Select state" /></Field>
          <Field label="Contact type"><Select value={form.contact_type} onChange={set('contact_type')} options={CONTACT_TYPES} /></Field>
        </FormGrid>
        <div className="form-actions"><SubmitBtn onClick={save} loading={loading}>Add contact</SubmitBtn></div>
        <SaveMessage {...msg} />
      </Card>

      <Card>
        <div className="card-header">
          <span className="card-title">All contacts</span>
          <select value={filter} onChange={e => setFilter(e.target.value)} className="field-select" style={{ width:'160px' }}>
            <option value="all">All types</option>
            {CONTACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        {!records.length ? <EmptyState text="No contacts found." /> :
          records.map(c => (
            <div key={c.id} className="list-row">
              <Avatar name={c.full_name} colorClass={AVATAR_COLORS[c.contact_type] || 'avatar-blue'} />
              <div className="list-row-info">
                <div className="list-row-name">{c.full_name}</div>
                <div className="list-row-meta">{c.company || '—'} · {c.state || '—'} · {c.email || 'no email'}</div>
                <RecordMeta submitter={c.submitted_by_name} date={c.created_at} />
              </div>
              <Badge variant={TYPE_VARIANTS[c.contact_type] || 'gray'}>{c.contact_type}</Badge>
            </div>
          ))
        }
      </Card>
    </div>
  );
}

const CO_TYPES = [{value:'client',label:'Operations client'},{value:'corporate-academy',label:'Corporate Academy'},{value:'referral',label:'Referral partner'},{value:'broker',label:'Insurance / risk'},{value:'prospect',label:'Prospect'}];

export function CompaniesPage({ profile }) {
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({ name:'', industry:'', states:'', company_type:'client', properties_count:'', ongoing_fw_need:'unknown' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ show:false, text:'', error:false });

  useEffect(() => { load(); }, []);
  async function load() {
    const { data } = await supabase.from('companies').select('*').order('created_at', { ascending:false });
    setRecords(data || []);
  }
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  async function save() {
    if (!form.name) { setMsg({ show:true, text:'Company name required.', error:true }); return; }
    setLoading(true);
    const { error } = await supabase.from('companies').insert({
      ...form,
      properties_count: form.properties_count ? parseInt(form.properties_count, 10) : null,
      submitted_by: profile.id, submitted_by_name: profile.full_name || profile.email
    });
    setLoading(false);
    if (error) { setMsg({ show:true, text:'Error: ' + error.message, error:true }); return; }
    setMsg({ show:true, text:'Company saved.', error:false });
    setForm({ name:'', industry:'', states:'', company_type:'client', properties_count:'', ongoing_fw_need:'unknown' });
    load();
    setTimeout(() => setMsg({ show:false }), 3000);
  }

  return (
    <div>
      <Card>
        <SectionLabel>ADD COMPANY</SectionLabel>
        <FormGrid>
          <Field label="Company name"><Input value={form.name} onChange={set('name')} /></Field>
          <Field label="Industry"><Select value={form.industry} onChange={set('industry')} options={INDUSTRIES} placeholder="Select industry" /></Field>
          <Field label="State(s)"><Input value={form.states} onChange={set('states')} placeholder="FL, NJ, TX" /></Field>
          <Field label="Company type"><Select value={form.company_type} onChange={set('company_type')} options={CO_TYPES} /></Field>
          <Field label="Properties managed (est.)"><Input type="number" value={form.properties_count} onChange={set('properties_count')} placeholder="# of properties" /></Field>
          <Field label="Ongoing FW need?"><Select value={form.ongoing_fw_need} onChange={set('ongoing_fw_need')} options={[{value:'yes',label:'Yes'},{value:'no',label:'No'},{value:'unknown',label:'Unknown'}]} /></Field>
        </FormGrid>
        <div className="form-actions"><SubmitBtn onClick={save} loading={loading}>Add company</SubmitBtn></div>
        <SaveMessage {...msg} />
      </Card>

      <Card>
        <CardHeader title="Companies" hint={records.length + ' total'} />
        {!records.length ? <EmptyState text="No companies yet." /> :
          <table className="data-table">
            <thead><tr><th>Company</th><th>Industry</th><th>States</th><th>Type</th><th>Properties</th><th>FW need</th></tr></thead>
            <tbody>
              {records.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight:'600' }}>{c.name}</td>
                  <td>{c.industry}</td><td>{c.states}</td><td>{c.company_type}</td>
                  <td>{c.properties_count || '—'}</td>
                  <td><Badge variant={c.ongoing_fw_need==='yes'?'green':c.ongoing_fw_need==='no'?'gray':'amber'}>{c.ongoing_fw_need==='yes'?'Yes':c.ongoing_fw_need==='no'?'No':'Unknown'}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </Card>
    </div>
  );
}
